// src/pages/DriverDashboard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./DriverDashboard.css";
import logo from "../images/logo.png"; // ✅ Proper import
import api from "../utils/api";


export default function DriverDashboard() {
  const [tab, setTab] = useState("available");
  const [deliveries, setDeliveries] = useState([]);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("driver_token"); // ✅ consistent
  const driver = (() => {
    try {
      return JSON.parse(localStorage.getItem("driver") || "{}");
    } catch {
      return {};
    }
  })();

  useEffect(() => {
    if (!token) {
      navigate("/driver/login");
      return;
    }

    if (driver.status === "approved") {
      fetchDeliveries(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchDeliveries = async (which) => {
    setLoading(true);
    setMsg("");
    try {
      let url = "";
      if (which === "available") url = "/driver/deliveries/available";
      else if (which === "upcoming") url = "/driver/deliveries/mine/upcoming";
      else if (which === "completed") url = "/driver/deliveries/mine/completed";
      else if (which === "cancelled") url = "/driver/deliveries/mine/cancelled";

      const res = await api.get(`${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeliveries(res.data || []);
    } catch (err) {
      console.error("Fetch deliveries error:", err);
      setMsg("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(
        `/driver/deliveries/${id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ✅ Redirect to the right tab after action
      if (action === "accept") setTab("upcoming");
      else if (action === "cancel") setTab("cancelled");
      else if (action === "picked-up") setTab("upcoming");
      else if (action === "complete") setTab("completed");
      else setTab("available");
    } catch (err) {
      alert(err.response?.data?.error || "Action failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("driver_token");
    localStorage.removeItem("driver");
    navigate("/driver/login");
  };

  // =====================
  // Pending / Rejected Dashboard
  // =====================
  if (driver.status !== "approved") {
    return (
      <div className="driver-dashboard-wrapper">
        <div className="dashboard-card text-center">
          <img src={logo} alt="HomeFoodGrab" className="logo mb-4" />
          <h1 className="text-2xl font-bold mb-4">Driver Dashboard</h1>

          {driver.status === "pending" && (
            <div className="pending-banner">
              🚦 Your application is still <b>Pending Approval</b>. <br />
              You can’t accept deliveries yet. <br />
              Please send documents to{" "}
              <a href="mailto:support@homefoodgrab.com">
                support@homefoodgrab.com
              </a>.
            </div>
          )}

          {driver.status === "rejected" && (
            <div className="rejected-banner">
              ❌ Unfortunately your application was <b>Rejected</b>. <br />
              For help, contact{" "}
              <a href="mailto:support@homefoodgrab.com">
                support@homefoodgrab.com
              </a>.
            </div>
          )}

          <p className="mb-4">
            Hi{" "}
            <span className="font-semibold">
              {driver.first_name || "Driver"}
            </span>
            , your account status is{" "}
            <span className="font-semibold">{driver.status || "unknown"}</span>.
          </p>

          <div className="flex justify-center gap-3">
            <button
              onClick={logout}
              className="px-4 py-2 border border-red-600 text-red-600 rounded hover:bg-red-600 hover:text-white"
            >
              🚪 Logout
            </button>
            <a
              href="https://homefoodgrab.com"
              className="px-4 py-2 border border-gray-600 text-gray-600 rounded hover:bg-gray-600 hover:text-white"
            >
              🏠 Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // =====================
  // Approved Dashboard
  // =====================
  return (
    <div className="driver-dashboard-wrapper">
      <div className="dashboard-card">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex flex-col items-center">
            <img src={logo} alt="HomeFoodGrab" className="logo mb-2" />
            <h1 className="text-3xl font-bold">Driver Dashboard</h1>
          </div>

          <button
            onClick={logout}
            className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-600 hover:text-white transition"
          >
            🚪 Logout
          </button>
        </div>

        <p className="mb-6 text-gray-700">
          Welcome,{" "}
          <span className="font-semibold">
            {driver.first_name} {driver.last_name}
          </span>
        </p>

        {/* Tabs */}
        <div className="flex gap-3 mb-6 justify-center border-b">
          {[
            { key: "available", label: "Available 🚚" },
            { key: "upcoming", label: "Upcoming 📅" },
            { key: "completed", label: "Completed ✅" },
            { key: "cancelled", label: "Cancelled ❌" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2 font-medium rounded-t-md transition ${
                tab === t.key
                  ? "bg-gray-800 text-white scale-105"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-black"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && <p className="text-center">Loading…</p>}
        {msg && <p className="text-center text-red-600">{msg}</p>}

        {/* Table */}
        {deliveries.length === 0 && !loading && (
          <p className="text-center text-gray-500">
            No deliveries in this category.
          </p>
        )}

        {deliveries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 bg-white rounded-lg shadow">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="px-4 py-3 border">Pay</th>
                  <th className="px-4 py-3 border">Pickup</th>
                  <th className="px-4 py-3 border">Dropoff</th>
                  <th className="px-4 py-3 border">Pickup Time</th>
                  <th className="px-4 py-3 border">Status</th>
                  <th className="px-4 py-3 border">Payout</th>
                  <th className="px-4 py-3 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">
                      £{(d.fee_pence / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 border">{d.pickup_postcode}</td>
                    <td className="px-4 py-2 border">{d.dropoff_postcode}</td>
                    <td className="px-4 py-2 border">
                      {new Date(d.pickup_time).toLocaleString("en-GB", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="px-4 py-2 border capitalize">{d.status}</td>
                    <td className="px-4 py-2 border">
                      {d.payout_status ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                          {d.payout_status}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-2 border space-x-2">
                      {d.status === "available" && (
                        <button
                          onClick={() => doAction(d.id, "accept")}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded"
                        >
                          ✅ Accept
                        </button>
                      )}
                      {d.status === "assigned" && (
                        <>
                          <button
                            onClick={() => doAction(d.id, "cancel")}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded"
                          >
                            ❌ Cancel
                          </button>
                          <button
                            onClick={() => doAction(d.id, "picked-up")}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded"
                          >
                            🚗 Picked Up
                          </button>
                        </>
                      )}
                      {d.status === "picked_up" && (
                        <button
                          onClick={() => doAction(d.id, "complete")}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded"
                        >
                          🏁 Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

